import type {
  PackageNotificationPackageType,
  PackageNotificationTriggerType,
} from "../contracts/triggers";
import { formatExpireDateLo } from "./format-expire-date-lo";

export type BuildNotificationMessageInput = {
  studentName: string;
  packageName: string;
  packageType: PackageNotificationPackageType;
  triggerType: PackageNotificationTriggerType;
  remainingSessions?: number | null;
  remainingHours?: number | null;
  expireDate: string;
  daysUntilExpiry?: number | null;
};

const DEFAULT_TEMPLATES = {
  low_sessions: `ສະບາຍດີ {studentName} — Fanglao Studio
ແພັກ {packageName} ຂອງທ່ານເຫຼືອ {remainingSessions} ຄັ້ງ
ຫາກຕ້ອງການຕໍ່ອາຍຸ ກະລຸນາຕິດຕໍ່ສຕູດິໂອ`,
  low_hours: `ສະບາຍດີ {studentName} — Fanglao Studio
ແພັກ {packageName} ຂອງທ່ານເຫຼືອ {remainingHours} ຊົ່ວໂມງ
ຫາກຕ້ອງການຕໍ່ອາຍຸ ກະລຸນາຕິດຕໍ່ສຕູດິໂອ`,
  expiring_soon: `ສະບາຍດີ {studentName} — Fanglao Studio
ແພັກ {packageName} ຂອງທ່ານຈະໝົດອາຍຸ {expireDate} (ອີກ {daysUntilExpiry} ວັນ)
ຫາກຕ້ອງການຕໍ່ອາຍຸ ກະລຸນາຕິດຕໍ່ສຕູດິໂອ`,
  combined: `ສະບາຍດີ {studentName} — Fanglao Studio
ແພັກ {packageName} ຂອງທ່ານເຫຼືອ {remainingLabel} (ໝົດອາຍຸ {expireDate})
ຫາກຕ້ອງການຕໍ່ອາຍຸ ກະລຸນາຕິດຕໍ່ສຕູດິໂອ`,
} as const;

function fallbackName(name: string): string {
  const trimmed = name.trim();
  return trimmed || "ລູກຄ້າ";
}

function fallbackPackage(name: string): string {
  const trimmed = name.trim();
  return trimmed || "ແພັກເກດ";
}

function formatRemainingHours(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "0";
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function remainingLabel(input: BuildNotificationMessageInput): string {
  if (input.packageType === "private_lesson") {
    return `${formatRemainingHours(input.remainingHours)} ຊົ່ວໂມງ`;
  }
  return `${input.remainingSessions ?? 0} ຄັ້ງ`;
}

function applyPlaceholders(
  template: string,
  values: Record<string, string>,
): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => values[key] ?? "");
}

export function buildNotificationMessage(
  input: BuildNotificationMessageInput,
): string {
  const studentName = fallbackName(input.studentName);
  const packageName = fallbackPackage(input.packageName);
  const expireDate = formatExpireDateLo(input.expireDate);
  const daysUntilExpiry =
    input.daysUntilExpiry != null ? String(input.daysUntilExpiry) : "0";

  const common = {
    studentName,
    packageName,
    expireDate,
    daysUntilExpiry,
    remainingSessions: String(input.remainingSessions ?? 0),
    remainingHours: formatRemainingHours(input.remainingHours),
    remainingLabel: remainingLabel(input),
  };

  if (input.triggerType === "combined") {
    return applyPlaceholders(DEFAULT_TEMPLATES.combined, common).trim();
  }

  const template = DEFAULT_TEMPLATES[input.triggerType];
  return applyPlaceholders(template, common).trim();
}

export function classifyPackageNotificationTrigger(input: {
  packageType: PackageNotificationPackageType;
  remainingSessions?: number;
  remainingHours?: number;
  expireDate: string;
  todayStr: string;
  thresholdSessions: number;
  thresholdHours: number;
  daysBeforeExpiry: number;
}): PackageNotificationTriggerType | null {
  const isLow =
    input.packageType === "package"
      ? (input.remainingSessions ?? 0) < input.thresholdSessions
      : (input.remainingHours ?? 0) < input.thresholdHours;

  const expiry = input.expireDate.slice(0, 10);
  const today = input.todayStr.slice(0, 10);
  const daysUntilExpiry = Math.ceil(
    (new Date(`${expiry}T00:00:00`).getTime() -
      new Date(`${today}T00:00:00`).getTime()) /
      (24 * 60 * 60 * 1000),
  );
  const isExpiring =
    daysUntilExpiry >= 0 && daysUntilExpiry <= input.daysBeforeExpiry;

  if (isLow && isExpiring) return "combined";
  if (isLow) {
    return input.packageType === "package" ? "low_sessions" : "low_hours";
  }
  if (isExpiring) return "expiring_soon";
  return null;
}
